/**
 * Lifecycle of a single wave, mirroring what Unity's `CircularWave` does with
 * time rather than with a state field.
 *
 * In the Unity build the phases are implicit: the object is created when the
 * scheduler reaches `event.time`, and `Update` returns early while
 * `elapsed - time - warningLeadTime < 0`, so the ring sits at radius zero until
 * its warning window expires. Naming the phases makes that behaviour legible and
 * gives the view something to key presentation off without re-deriving it.
 */
export const WaveState = {
  /** Not in use; a free slot in the pool. */
  Idle: 0,
  /**
   * Spawned and visible as a marker, but not yet expanding.
   *
   * This is the requirement document's "発生前予告": the player is shown *where*
   * a wave will open before it starts moving.
   */
  Warning: 1,
  /** Radius is growing from `initialRadius` at `speed`. */
  Expanding: 2,
  /**
   * Judged, and playing out its dissolve before leaving the pool.
   *
   * The radius is frozen here. A wave that kept growing while it collapsed
   * would be two animations fighting, and more importantly it would still be
   * moving after the moment the player was graded on — the picture has to stop
   * saying anything new once the outcome is decided.
   */
  Resolving: 3,
  /** Grew past the play area and was released. */
  Retired: 4,
  /**
   * Scheduled and holding a pool slot, but not yet on screen.
   *
   * Only reachable below 1× wave speed, where a wave has to appear *earlier*
   * than its own chart event to give the player the longer approach they asked
   * for. The scheduler therefore runs ahead of the clock and parks the wave
   * here until its visual spawn time arrives. A pending wave is invisible and
   * untappable — as far as the game is concerned it has not happened yet.
   */
  Pending: 5,
} as const;

export type WaveState = (typeof WaveState)[keyof typeof WaveState];

/**
 * The states in which a wave is on screen — and therefore tappable.
 *
 * An **allow-list**, deliberately, and this is worth the paragraph. The rule
 * used to be written as "not `Pending`" at each site that needed it, and it was
 * also written as `state !== 0` in a diagnostic probe. `Pending` is 5; 0 is
 * `Idle`. That probe therefore selected waves the renderer never draws, looked
 * at where they were not, and concluded the hit target had disappeared — a
 * whole investigation aimed at the wrong thing.
 *
 * A deny-list is only correct if you remember every value it has to deny. A
 * list of what *is* drawn is correct by construction, and a new state added to
 * the enum is invisible until someone deliberately adds it here.
 *
 * `Resolving` is on the list: a judged wave is still dissolving on screen.
 * `Idle` and `Retired` are not on it, but they are also never `active`.
 */
export const VISIBLE_WAVE_STATES: readonly WaveState[] = [
  WaveState.Warning,
  WaveState.Expanding,
  WaveState.Resolving,
];

export function isVisibleWaveState(state: WaveState): boolean {
  return VISIBLE_WAVE_STATES.includes(state);
}

/**
 * Whether a wave is on screen.
 *
 * The single answer used by the renderer, by the hit test, and by anything that
 * inspects the field. If those three ever disagree, the player is being asked to
 * press something they cannot see, or something they can see is refusing them.
 */
export function isWaveVisible(wave: { readonly active: boolean; readonly state: WaveState }): boolean {
  return wave.active && isVisibleWaveState(wave.state);
}

export function waveStateName(state: WaveState): string {
  switch (state) {
    case WaveState.Idle:
      return 'idle';
    case WaveState.Warning:
      return 'warning';
    case WaveState.Expanding:
      return 'expanding';
    case WaveState.Resolving:
      return 'resolving';
    case WaveState.Retired:
      return 'retired';
    case WaveState.Pending:
      return 'pending';
  }
}
